import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { Navigate, useNavigate } from 'react-router';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import { useAuth } from '../../hooks/useAuth';
import routes from '../../routes/routes';
import { terminalBarIconButtonSx, terminalSelectSx } from '../../theme/terminalStyles';
import {
  useCreateIncomingAiInvoiceMutation,
  useListIncomingAiInvoicesQuery,
} from '../../store/api/incomingAiInvoicesApi';
import IncomingAiInvoiceCard from './IncomingAiInvoiceCard';
import { fileToImagePayload } from './incomingAiInvoiceUtils';

const defaultDateFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 10);
  return d.toISOString().slice(0, 10);
};

const IncomingAiInvoiceListPage = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { authState } = useAuth();
  const user = authState.user;
  const [numberContains, setNumberContains] = useState('');
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [showSent, setShowSent] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [createError, setCreateError] = useState('');

  const queryParams = useMemo(
    () => ({ numberContains, dateFrom, showSent: showSent ? '1' : '0' }),
    [numberContains, dateFrom, showSent]
  );
  const { data = [], isFetching, error, refetch } = useListIncomingAiInvoicesQuery(queryParams);
  const [createInvoice, { isLoading: isCreating }] = useCreateIncomingAiInvoiceMutation();

  if (!user?.terminalWorkplace?.isIncomingInvoicesAi) {
    return <Navigate to={routes.root} replace />;
  }

  const handleCreate = async () => {
    setCreateError('');
    try {
      const images = await Promise.all(files.map(fileToImagePayload));
      const doc = await createInvoice({ images }).unwrap();
      setCreateOpen(false);
      setFiles([]);
      navigate(`${routes.incomingAiInvoices}/${doc.id}`);
    } catch (err) {
      setCreateError(err?.data?.message || err?.message || 'Не вдалося створити документ');
    }
  };

  const bottomBar = (
    <>
      <IconButton aria-label="додати" onClick={() => setCreateOpen(true)} sx={terminalBarIconButtonSx}>
        <AddIcon />
      </IconButton>
      <IconButton aria-label="оновити" onClick={refetch} sx={terminalBarIconButtonSx}>
        <RefreshIcon />
      </IconButton>
    </>
  );

  return (
    <TerminalLayout
      title="Вхідні накладні AI"
      titleIcon={<ReceiptLongOutlinedIcon />}
      backTo={routes.root}
      bottomBar={bottomBar}
    >
      <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }} elevation={0}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 150px' }, gap: 1 }}>
          <TextField
            label="Номер"
            size="small"
            value={numberContains}
            onChange={(e) => setNumberContains(e.target.value)}
            sx={terminalSelectSx}
          />
          <TextField
            label="Дата з"
            size="small"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            sx={terminalSelectSx}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <FormControlLabel
          control={<Switch checked={showSent} onChange={(e) => setShowSent(e.target.checked)} />}
          label="Показати передані в ERP"
        />
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 1 }}>{error?.data?.message || 'Помилка завантаження'}</Alert> : null}
      {isFetching ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box> : null}
      {!isFetching && data.length === 0 ? (
        <Alert severity="info">Немає вхідних накладних AI за вибраними фільтрами.</Alert>
      ) : null}
      {data.map((doc) => (
        <IncomingAiInvoiceCard
          key={doc.id}
          doc={doc}
          onOpen={() => navigate(`${routes.incomingAiInvoices}/${doc.id}`)}
        />
      ))}

      <Dialog open={createOpen} onClose={() => (isCreating ? null : setCreateOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle>Нова вхідна накладна AI</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Додайте одне або кілька фото накладної. Якщо документ на кількох сторінках, додайте всі фото одразу.
          </Typography>
          <input
            ref={inputRef}
            hidden
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={(e) => setFiles([...e.target.files])}
          />
          <TerminalOrangeButton onClick={() => inputRef.current?.click()} disabled={isCreating}>
            Вибрати або сфотографувати
          </TerminalOrangeButton>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Фото: <strong>{files.length}</strong>
          </Typography>
          {files.map((file) => (
            <Typography key={`${file.name}-${file.size}`} variant="body2" color="text.secondary" noWrap>
              {file.name}
            </Typography>
          ))}
          {createError ? <Alert severity="error" sx={{ mt: 1.5 }}>{createError}</Alert> : null}
          {isCreating ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">AI розпізнає документ...</Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <TerminalOrangeButton onClick={() => setCreateOpen(false)} disabled={isCreating}>
            Скасувати
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleCreate} disabled={isCreating || files.length === 0}>
            Розпізнати
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>
    </TerminalLayout>
  );
};

export default IncomingAiInvoiceListPage;
